import React from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import SearchIcon from '@atlaskit/icon/core/search';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

type ColdStartButtonProps = {
	/**
	 * Icon element to display before the button text
	 */
	icon: React.ReactNode;
	/**
	 * Button text/label to display
	 */
	buttonText: string;
	/**
	 * Optional onClick handler
	 */
	onClick?: () => void;
};

const buttonStyles = css({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	alignItems: 'center',
	gap: '6px',
	padding: `0px ${token('space.100')}`,
	borderRadius: '4px',
	border: `1px solid ${wacToken('color.neutral.light300')}`,
	background: wacToken('color.neutral.light100'),
	cursor: 'pointer',
	transition: 'all 0.2s ease-in-out',
	minHeight: '32px',
	'&:hover': {
		background: wacToken('color.neutral.light200'),
		borderColor: wacToken('color.neutral.light600'),
	},
	'&:focus-visible': {
		background: wacToken('color.neutral.light100'),
		borderColor: wacToken('color.neutral.light300'),
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
	'&:active': {
		background: wacToken('color.neutral.light300'),
		borderColor: wacToken('color.neutral.light600'),
	},
});

const iconStyles = css({
	width: '16px',
	height: '16px',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const textStyles = css({
	fontFamily: 'Charlie Text, sans-serif',
	fontWeight: 400,
	fontSize: '14px',
	lineHeight: '1.4285714285714286em',
	textAlign: 'center',
	color: wacToken('color.neutral.light800'),
	margin: 0,
	padding: 0,
	whiteSpace: 'nowrap',
});

const buttonsContainerStyles = css({
	display: 'flex',
	flexDirection: 'row',
	gap: '8px',
	padding: '16px 12px',
	width: '100%',
	justifyContent: 'center',
	flexWrap: 'wrap',
});

type ColdStartButtonOption = {
	icon: React.ReactNode;
	labelId: string;
	label: string;
	prompt: string;
};

type ColdStartButtonsRowProps = {
	onButtonClick?: (prompt: string) => void;
	onButtonHover?: (prompt: string) => void;
	onButtonHoverEnd?: () => void;
};

export const ColdStartButton: React.FC<ColdStartButtonProps> = ({ icon, buttonText, onClick }) => {
	return (
		<button css={buttonStyles} onClick={onClick} type="button">
			<div css={iconStyles}>{icon}</div>
			<span css={textStyles}>{buttonText}</span>
		</button>
	);
};

/**
 * ColdStartButtonsRow displays a row of pre-filled action buttons for the Rovo Chat
 */
export const ColdStartButtonsRow: React.FC<ColdStartButtonsRowProps> = ({
	onButtonClick,
	onButtonHover,
	onButtonHoverEnd,
}) => {
	const intl = useIntl();

	const coldStartButtons: ColdStartButtonOption[] = [
		{
			icon: <ChartTrendUpIcon label="Chart trend up" />,
			labelId: 'RovoChat.coldStart.stayUpToDate',
			label: intl.formatMessage({
				id: 'RovoChat.coldStart.stayUpToDate',
				defaultMessage: 'Stay up to date',
			}),
			prompt: intl.formatMessage({
				id: 'RovoChat.coldStart.stayUpToDatePrompt',
				defaultMessage: "What's new this week that I should know about?",
			}),
		},
		{
			icon: <SearchIcon label="Search" />,
			labelId: 'RovoChat.coldStart.findInformation',
			label: intl.formatMessage({
				id: 'RovoChat.coldStart.findInformation',
				defaultMessage: 'Find information',
			}),
			prompt: intl.formatMessage({
				id: 'RovoChat.coldStart.findInformationPrompt',
				defaultMessage: 'Help me find information about [topic or process]',
			}),
		},
		{
			icon: <LightbulbIcon label="Lightbulb" />,
			labelId: 'RovoChat.coldStart.brainstormIdeas',
			label: intl.formatMessage({
				id: 'RovoChat.coldStart.brainstormIdeas',
				defaultMessage: 'Brainstorm ideas',
			}),
			prompt: intl.formatMessage({
				id: 'RovoChat.coldStart.brainstormIdeasPrompt',
				defaultMessage: "Let's brainstorm ideas for [project, campaign, or topic].",
			}),
		},
	];

	return (
		<div css={buttonsContainerStyles}>
			{coldStartButtons.map((button) => {
				return (
					<div
						key={button.labelId}
						onMouseEnter={() => onButtonHover?.(button.prompt)}
						onMouseLeave={() => onButtonHoverEnd?.()}
						onFocus={() => onButtonHover?.(button.prompt)}
						onBlur={() => onButtonHoverEnd?.()}
					>
						<ColdStartButton
							icon={button.icon}
							buttonText={button.label}
							onClick={() => onButtonClick?.(button.prompt)}
						/>
					</div>
				);
			})}
		</div>
	);
};
